import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { styled } from '@mui/material';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { AgisticsColors } from '../../../../styles/agistics-colors';
import type { Load, LocationGroup, OrganizationLocation, Stop } from '../../../../types';
import { filterStopByDestination, filterStopByOrigin } from '../../../../utils/data-mapping-utils';
import { EstimatedTotalTrip } from '../../drafts-loads-table-cells/loads/display/estimated-total-trip';
import { LoadCargo } from '../../drafts-loads-table-cells/loads/display/load-cargo';
import { LoadOrderNumbers } from '../../drafts-loads-table-cells/loads/display/load-order-numbers';
import { LoadStopLocation } from '../../drafts-loads-table-cells/loads/display/load-stop-location';
import { LoadStopTime } from '../../drafts-loads-table-cells/loads/display/load-stop-time';
import { PerformanceExpander } from '../../drafts-loads-table-cells/performance-expander';
import { PerformanceDragIcon } from '../../drafts-loads-table-cells/performance-icons';
import { genDestinationIds, genOriginIds } from '../../utils/drafts-loads-utils';
import { BulkScheduleDraftColumn } from './bulk-schedule';
import { BulkScheduleStops } from './bulk-schedule-stops';

interface BulkScheduleDraftProps {
    draft: Load;
    hoveredDraftIdState: {
        hoveredDraftId: number | null;
        setHoveredDraftId: (draftId: number | null) => void;
    }
    onUpdateStops: (s: Stop[]) => void;
    driverName: string;
}

const DraftContainer = styled('div')({
    boxShadow: '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)'
});

export const BulkScheduleDraft = (props: BulkScheduleDraftProps) => {
    const { draft, hoveredDraftIdState, onUpdateStops, driverName } = props;
    const { hoveredDraftId, setHoveredDraftId } = hoveredDraftIdState;

    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: locationGroups = [] } = useOrganizationQuery<LocationGroup[]>(OrgQueryKeys.locationGroups);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: draft.id });
    const [isExpanded, setIsExpanded] = React.useState(false);

    const originIds = genOriginIds(draft);
    const uniqueOriginIds = [...new Set(originIds)];
    const origins = draft.stops.filter(filterStopByOrigin);

    const destinationIds = genDestinationIds(draft);
    const uniqueDestinationIds = [...new Set(destinationIds)];
    const destinations = draft.stops.filter(filterStopByDestination);

    const dragStyle = {
        transform: CSS.Translate.toString(transform),
        transition: transition,
    };

    return (
        <DraftContainer
            ref={setNodeRef}
            style={{
                ...dragStyle,
                background: draft.id === hoveredDraftId ? AgisticsColors.lightGray : undefined,
            }}
            onMouseEnter={() => setHoveredDraftId(draft.id)}
            onMouseLeave={() => setHoveredDraftId(null)}
        >
            <BulkScheduleDraftColumn>
                <PerformanceDragIcon
                    isDragging={isDragging}
                    listeners={listeners}
                    attributes={attributes}
                    isCarrier={false}
                />
                <LoadStopTime time={draft.date} />
                <LoadOrderNumbers load={draft} />
                <div><LoadCargo load={draft} /></div>
                <div>
                    {uniqueOriginIds.map(uniqueLocationId => (
                        <LoadStopLocation
                            key={uniqueLocationId}
                            organizationLocationId={uniqueLocationId}
                            organizationLocations={organizationLocations}
                            locationGroups={locationGroups}
                            stops={origins}
                        />
                    ))}
                </div>
                <div>
                    {uniqueDestinationIds.map(uniqueLocationId => (
                        <LoadStopLocation
                            key={uniqueLocationId}
                            organizationLocationId={uniqueLocationId}
                            organizationLocations={organizationLocations}
                            locationGroups={locationGroups}
                            stops={destinations}
                        />
                    ))}
                </div>
                <EstimatedTotalTrip
                    mode='time' // todo: respect user preference?
                    load={draft} 
                    style={{ display: 'flex', justifyContent: 'center' }}
                />
                <div>{driverName || L.none()}</div>
                <PerformanceExpander
                    expanded={isExpanded}
                    onChange={() => setIsExpanded((prev) => !prev)}
                />
            </BulkScheduleDraftColumn>
            
            {isExpanded && <BulkScheduleStops draftStops={draft.stops} onUpdateStops={onUpdateStops} />}
        </DraftContainer>
    );
};
